import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement } from 'react';

import { ChartFilter, DateRangeObject } from 'shared/types/analytics';
import {
  startOfDayToUtcMillis,
  endOfDayToUtcMillis,
  millisToDateTime,
} from '../../../../shared/utils/date/dates';
import { useProjectTimezone } from 'store/project';
import { DateRangePickerBase, DateRangeValue } from 'components/common/dateRangePicker';
import { customDateRangeAdapterStyles } from './CustomDateRangeAdapter.styles';
import { isCompleteDateRange } from './model';

export function CustomDateRangeAdapter(props: {
  filter: ChartFilter<unknown>;
  localSetter: (value: any) => void;
  localValue: DateRangeObject;
  disabled: boolean;
}): ReactElement {
  const timezone = useProjectTimezone();
  const { localValue, localSetter, disabled } = props;
  // A range still being picked (one day chosen, a year half typed) stays here:
  // the charts get only complete ranges.
  const [pickedRange, setPickedRange] = useState<DateRangeObject>();
  const shownRange = pickedRange ?? localValue;

  useEffect(() => {
    setPickedRange(undefined);
  }, [localValue.from, localValue.to]);

  // Convert DateRangeObject (ISO strings) to DateRangeValue (milliseconds)
  const currentValue = useMemo((): DateRangeValue => {
    const fromDateTime =
      shownRange.from && shownRange.from.trim()
        ? DateTime.fromISO(shownRange.from, { zone: timezone })
        : null;
    const toDateTime =
      shownRange.to && shownRange.to.trim()
        ? DateTime.fromISO(shownRange.to, { zone: timezone })
        : null;

    // Validate that DateTime objects are valid
    const validFromDateTime =
      fromDateTime && fromDateTime.isValid ? fromDateTime : null;
    const validToDateTime =
      toDateTime && toDateTime.isValid ? toDateTime : null;

    return {
      start: startOfDayToUtcMillis(validFromDateTime),
      end: endOfDayToUtcMillis(validToDateTime),
    };
  }, [shownRange.from, shownRange.to, timezone]);

  const handleChange = useCallback(
    (value: DateRangeValue): void => {
      // Convert back to DateRangeObject (ISO strings)
      const startDateTime = millisToDateTime(value.start, timezone);
      const endDateTime = millisToDateTime(value.end, timezone);

      const newDateRange: DateRangeObject = {
        from: startDateTime?.toISODate() ?? '',
        to: endDateTime?.toISODate() ?? '',
      };

      if (isCompleteDateRange(newDateRange)) {
        setPickedRange(undefined);
        localSetter(newDateRange);
      } else {
        setPickedRange(newDateRange);
      }
    },
    [localSetter, timezone]
  );

  return (
    <Box sx={customDateRangeAdapterStyles}>
      <Box sx={{ width: '100%' }}>
        <DateRangePickerBase
          value={currentValue}
          onChange={handleChange}
          disabled={disabled}
          fieldSize="medium"
          sx={{ width: '100%' }}
        />
      </Box>
    </Box>
  );
}

